import React, { useEffect, useLayoutEffect, useRef } from 'react';
import {
    Chip,
    ChipHandle,
    ChipList as KendoChipList,
    ChipListHandle,
    ChipListProps,
    ChipProps,
} from '@progress/kendo-react-buttons';
import cn from 'clsx';
import InactiveIcon from '@components-clean/core/icons/inactive-icon/inactive-icon';
import { useReadOnlyContext } from '@contexts-clean/read-only-context';
import { textToId } from '@utils/automation-test-helpers';

interface ChipWithTestIdProps extends ChipProps {
    activeField?: string;
    ignoreReadonlyContext?: boolean | null;
}

interface IProps extends ChipListProps {
    'data-testid': string;
    secondary?: boolean;
    activeField?: string;
    ignoreReadonlyContext?: boolean | null;
}

const ChipWithTestId = ({
    disabled,
    activeField,
    dataItem,
    className,
    ignoreReadonlyContext,
    ...chipProps
}: ChipWithTestIdProps) => {
    const { readOnly: readOnlyCtx } = useReadOnlyContext();

    const chipHandleReference = useRef<ChipHandle>(null);

    useEffect(() => {
        const testId = chipHandleReference.current?.element?.parentElement?.getAttribute('data-testid');
        chipHandleReference.current?.element?.setAttribute(
            'data-testid',
            `${testId}-${textToId(chipProps.text ?? '')}`,
        );
        return () => {
            chipHandleReference.current = null;
        };
    }, [chipProps.text]);

    // If `activeField` defined and refers to a boolean property on `dataItem`, use it. Otherwise, default to true
    const active =
        activeField != null && activeField in dataItem && typeof dataItem[activeField] === 'boolean'
            ? dataItem[activeField]
            : true;

    // Optional trailing content (e.g. an icon) supplied per data item via `endAdornment`.
    // Rendered inside the chip after the text. Give it its own onClick + stopPropagation
    // if it should act independently of chip selection.
    const endAdornment = dataItem?.endAdornment as React.ReactNode;

    return (
        <Chip
            ref={chipHandleReference}
            disabled={disabled || (readOnlyCtx && !ignoreReadonlyContext)}
            dataItem={dataItem}
            className={cn(
                {
                    inactiveMultiSelectTag: !active,
                },
                className,
            )}
            {...chipProps}
        >
            <>
                {active ? (
                    chipProps.text
                ) : (
                    <span className="inactiveMultiSelectTagContent">
                        <InactiveIcon dataTestId="inactiveIcon" tooltipPosition="top" isSuffix padding="none" />
                        {chipProps.text}
                    </span>
                )}
                {endAdornment}
            </>
        </Chip>
    );
};

const ChipList = ({
    'data-testid': testId,
    selection,
    secondary,
    className,
    activeField,
    ignoreReadonlyContext,
    ...props
}: IProps) => {
    const chipListHandleReference = useRef<ChipListHandle>(null);
    if (!selection) selection = 'single';

    useLayoutEffect(() => {
        chipListHandleReference.current?.element?.setAttribute('data-testid', testId);
        return () => {
            chipListHandleReference.current = null;
        };
    }, [testId]);

    return (
        <KendoChipList
            ref={chipListHandleReference}
            selection={selection}
            chip={(chipProps) => (
                <ChipWithTestId
                    activeField={selection === 'none' ? activeField : undefined}
                    ignoreReadonlyContext={ignoreReadonlyContext}
                    {...chipProps}
                />
            )}
            className={cn('chip-list', { plum: secondary }, className)}
            {...props}
        />
    );
};

export default ChipList;
